// ═══════════════════════════════════════════════════════════════
// 📘 MÓDULO 9 - UNIDAD 4: MAKING REQUESTS (Can/Could/Will/Would)
// ═══════════════════════════════════════════════════════════════
// ✅ Biblia v4.1 - Context a nivel SET, campo "questions"
// ✅ Progresión: A2+ → B2+ (informal → very formal)
// ✅ Narrativa: Hospital + Daily Life Requests
// ═══════════════════════════════════════════════════════════════

export const M9_UNIT_4 = {
  id: "m9-u4-requests",
  title: "Unidad 4: Making Requests",
  topic: "Make polite requests using Can you, Could you, Will you, Would you, and Would you mind in medical and everyday situations.",
  iconType: "hand-point-up",

  sets: [
    // ───────────────────────────────────────────────────────────
    // 🙋 SET 1: CAN YOU...? - Informal Requests (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s1",
      title: "Set 1: Can You...? - Informal Requests",
      icon: "🙋",
      context: `**City Hospital - Nurse Station (Informal Environment)**

"CAN YOU...?" es la forma MÁS INFORMAL de pedir favores. Se usa con amigos, familia, colegas cercanos, o en situaciones casuales.

**City Hospital - Nurse Station (Informal Environment)**

"CAN YOU...?" is the MOST INFORMAL way to ask for favors. Used with friends, family, close colleagues, or casual situations.`,

      questions: [
        {
          type: "mc",
          q: "**Nurse to colleague:** '___ you help me move this patient to the wheelchair?'",
          options: ["Can", "Could", "Would", "Should"],
          a: "Can",
          successTip: "🙋 **CAN YOU...?** = informal request to someone you know well!"
        },
        {
          type: "mc",
          q: "**Doctor to nurse (working together daily):** '___ you bring me the patient's chart?'",
          options: ["Can", "Could", "Would", "May"],
          a: "Can",
          successTip: "📋 Perfect! **CAN YOU** = casual request between colleagues!"
        },
        {
          type: "input",
          q: "**Patient to friend visiting:** '___ you hand me that glass of water?'",
          alts: ["Can"],
          a: "Can",
          successTip: "💧 Great! **CAN YOU** is perfect for informal situations!"
        },
        {
          type: "mc",
          q: "**Nurse to assistant:** '___ you check the patient's temperature?'",
          options: ["Can", "Could", "Would", "Should"],
          a: "Can",
          successTip: "🌡️ Excellent! **CAN YOU** = direct, informal instruction!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Puedes pasarme esa toalla?'",
          words: ["Can", "you", "pass", "me", "that", "towel?"],
          extra: ["Could", "Would", "Will"],
          a: ["Can", "you", "pass", "me", "that", "towel?"],
          successTip: "💡 **CAN YOU + base verb** = simple informal request structure!"
        },
        {
          type: "mc",
          q: "**Receptionist to coworker:** '___ you answer the phone? I'm with a patient.'",
          options: ["Can", "Could", "Would", "Should"],
          a: "Can",
          successTip: "📞 Perfect! **CAN YOU** = quick request between colleagues!"
        },
        {
          type: "input",
          q: "**Doctor to intern (casual):** '___ you take notes during this examination?'",
          alts: ["Can"],
          a: "Can",
          successTip: "📝 Great! **CAN YOU** works for routine work requests!"
        },
        {
          type: "mc",
          q: "**Patient to family member:** '___ you turn on the TV for me?'",
          options: ["Can", "Could", "Would", "May"],
          a: "Can",
          successTip: "📺 Excellent! **CAN YOU** = informal family request!"
        },
        {
          type: "mc",
          q: "**Nurse to volunteer:** '___ you show this visitor where the cafeteria is?'",
          options: ["Can", "Could", "Would", "Should"],
          a: "Can",
          successTip: "🚶 Perfect! **CAN YOU** = simple, direct request!"
        },
        {
          type: "mc",
          q: "**Doctor to colleague (friends):** '___ you cover my shift tomorrow? I have a family emergency.'",
          options: ["Can", "Could", "Would", "Should"],
          a: "Can",
          successTip: "🏥 Outstanding! **CAN YOU** = informal request between friends!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎩 SET 2: COULD YOU...? - Polite Requests (A2+/B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s2",
      title: "Set 2: Could You...? - Polite Requests",
      icon: "🎩",
      context: `**City Hospital - Patient Care (Polite Environment)**

"COULD YOU...?" es MÁS EDUCADO que "can you". Se usa con pacientes, personas que no conoces bien, o cuando quieres ser más cortés.

**City Hospital - Patient Care (Polite Environment)**

"COULD YOU...?" is MORE POLITE than "can you". Used with patients, people you don't know well, or when you want to be more courteous.`,

      questions: [
        {
          type: "mc",
          q: "**Nurse to patient:** '___ you roll up your sleeve so I can take your blood pressure?'",
          options: ["Could", "Can", "Will", "Should"],
          a: "Could",
          successTip: "🎩 **COULD YOU...?** = polite request to patient!"
        },
        {
          type: "mc",
          q: "**Doctor to patient's family:** '___ you wait in the waiting room during the examination?'",
          options: ["Could", "Can", "Will", "May"],
          a: "Could",
          successTip: "🚪 Perfect! **COULD YOU** = respectful request to visitors!"
        },
        {
          type: "input",
          q: "**Receptionist to patient:** '___ you fill out this form, please?'",
          alts: ["Could"],
          a: "Could",
          successTip: "📋 Great! **COULD YOU** = polite professional request!"
        },
        {
          type: "mc",
          q: "**Pharmacist to customer:** '___ you sign here, please?'",
          options: ["Could", "Can", "Will", "Should"],
          a: "Could",
          successTip: "✍️ Excellent! **COULD YOU** = polite business request!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Podrías abrir la boca, por favor?'",
          words: ["Could", "you", "open", "your", "mouth,", "please?"],
          extra: ["Can", "Would", "Will"],
          a: ["Could", "you", "open", "your", "mouth,", "please?"],
          successTip: "💡 **COULD YOU + base verb + PLEASE** = polite medical request!"
        },
        {
          type: "mc",
          q: "**Doctor to new patient:** '___ you describe your symptoms for me?'",
          options: ["Could", "Can", "Will", "Should"],
          a: "Could",
          successTip: "🩺 Perfect! **COULD YOU** = polite professional inquiry!"
        },
        {
          type: "input",
          q: "**Nurse to elderly patient:** '___ you sit down here, please?'",
          alts: ["Could"],
          a: "Could",
          successTip: "💺 Great! **COULD YOU** shows respect to elderly patients!"
        },
        {
          type: "mc",
          q: "**Lab technician to patient:** '___ you remain still for a moment?'",
          options: ["Could", "Can", "Will", "May"],
          a: "Could",
          successTip: "🔬 Excellent! **COULD YOU** = polite professional instruction!"
        },
        {
          type: "mc",
          q: "**Receptionist to visitor:** '___ you show me your ID, please?'",
          options: ["Could", "Can", "Will", "Should"],
          a: "Could",
          successTip: "🪪 Perfect! **COULD YOU** = polite security request!"
        },
        {
          type: "mc",
          q: "**Doctor to patient:** '___ you lie down on the examination table?'",
          options: ["Could", "Can", "Will", "Should"],
          a: "Could",
          successTip: "🛏️ Outstanding! **COULD YOU** = respectful medical request!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 💬 SET 3: WILL YOU...? - Neutral Requests (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s3",
      title: "Set 3: Will You...? - Neutral Requests",
      icon: "💬",
      context: `**City Hospital - General Requests (Neutral Tone)**

"WILL YOU...?" es una forma NEUTRA de pedir favores. No es muy formal ni muy informal. Se usa en situaciones cotidianas y trabajo.

**City Hospital - General Requests (Neutral Tone)**

"WILL YOU...?" is a NEUTRAL way to ask for favors. Not very formal or very informal. Used in everyday situations and work.`,

      questions: [
        {
          type: "mc",
          q: "**Supervisor to employee:** '___ you organize these files by date?'",
          options: ["Will", "Could", "Can", "Should"],
          a: "Will",
          successTip: "💬 **WILL YOU...?** = neutral work instruction!"
        },
        {
          type: "mc",
          q: "**Doctor to nurse:** '___ you call the patient's family and update them?'",
          options: ["Will", "Could", "Can", "May"],
          a: "Will",
          successTip: "📞 Perfect! **WILL YOU** = neutral professional request!"
        },
        {
          type: "input",
          q: "**Manager to staff:** '___ you attend the meeting this afternoon?'",
          alts: ["Will"],
          a: "Will",
          successTip: "🏢 Great! **WILL YOU** = neutral request for participation!"
        },
        {
          type: "mc",
          q: "**Nurse to orderly:** '___ you take this equipment to storage?'",
          options: ["Will", "Could", "Can", "Should"],
          a: "Will",
          successTip: "📦 Excellent! **WILL YOU** = neutral work task!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Revisarás estos documentos mañana?'",
          words: ["Will", "you", "review", "these", "documents", "tomorrow?"],
          extra: ["Could", "Can", "Would"],
          a: ["Will", "you", "review", "these", "documents", "tomorrow?"],
          successTip: "💡 **WILL YOU + base verb** = neutral future request!"
        },
        {
          type: "mc",
          q: "**Administrator to doctor:** '___ you submit your report by Friday?'",
          options: ["Will", "Could", "Can", "May"],
          a: "Will",
          successTip: "📄 Perfect! **WILL YOU** = neutral deadline request!"
        },
        {
          type: "input",
          q: "**Team leader to member:** '___ you present the findings at the conference?'",
          alts: ["Will"],
          a: "Will",
          successTip: "🎤 Great! **WILL YOU** = neutral assignment request!"
        },
        {
          type: "mc",
          q: "**Coordinator to volunteers:** '___ you help with patient registration tomorrow?'",
          options: ["Will", "Could", "Can", "Should"],
          a: "Will",
          successTip: "✍️ Excellent! **WILL YOU** = neutral volunteer request!"
        },
        {
          type: "mc",
          q: "**Supervisor to technician:** '___ you calibrate the equipment before we start?'",
          options: ["Will", "Could", "Can", "May"],
          a: "Will",
          successTip: "⚙️ Perfect! **WILL YOU** = neutral technical instruction!"
        },
        {
          type: "mc",
          q: "**Manager to assistant:** '___ you schedule appointments for next week?'",
          options: ["Will", "Could", "Can", "Should"],
          a: "Will",
          successTip: "📅 Outstanding! **WILL YOU** = neutral administrative request!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 👔 SET 4: WOULD YOU...? - Formal Requests (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s4",
      title: "Set 4: Would You...? - Formal Requests",
      icon: "👔",
      context: `**City Hospital - Formal Situations (Professional)**

"WOULD YOU...?" es MÁS FORMAL que "will you". Se usa con superiores, personas importantes, o situaciones que requieren más cortesía.

**City Hospital - Formal Situations (Professional)**

"WOULD YOU...?" is MORE FORMAL than "will you". Used with superiors, important people, or situations requiring more courtesy.`,

      questions: [
        {
          type: "mc",
          q: "**Junior doctor to senior consultant:** '___ you review my diagnosis, please?'",
          options: ["Would", "Will", "Can", "Could"],
          a: "Would",
          successTip: "👔 **WOULD YOU...?** = very formal request to superior!"
        },
        {
          type: "mc",
          q: "**Receptionist to VIP patient:** '___ you follow me to the private consultation room?'",
          options: ["Would", "Will", "Can", "May"],
          a: "Would",
          successTip: "🚶 Perfect! **WOULD YOU** = formal service to VIP!"
        },
        {
          type: "input",
          q: "**Nurse to hospital director:** '___ you like to see the patient records?'",
          alts: ["Would"],
          a: "Would",
          successTip: "📊 Great! **WOULD YOU LIKE** = very polite formal offer!"
        },
        {
          type: "mc",
          q: "**Doctor to patient's lawyer:** '___ you please sign this consent form?'",
          options: ["Would", "Will", "Can", "Should"],
          a: "Would",
          successTip: "⚖️ Excellent! **WOULD YOU** = formal legal request!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Le gustaría sentarse, por favor?'",
          words: ["Would", "you", "like", "to", "sit", "down,", "please?"],
          extra: ["Will", "Can", "Could"],
          a: ["Would", "you", "like", "to", "sit", "down,", "please?"],
          successTip: "💡 **WOULD YOU LIKE TO** = extremely polite formal request!"
        },
        {
          type: "mc",
          q: "**Administrator to board member:** '___ you be able to attend the meeting on Tuesday?'",
          options: ["Would", "Will", "Can", "Could"],
          a: "Would",
          successTip: "🏢 Perfect! **WOULD YOU BE ABLE** = very formal inquiry!"
        },
        {
          type: "input",
          q: "**Surgeon to hospital CEO:** '___ you approve this new procedure protocol?'",
          alts: ["Would"],
          a: "Would",
          successTip: "📋 Great! **WOULD YOU** = formal request for authorization!"
        },
        {
          type: "mc",
          q: "**Nurse to visiting professor:** '___ you mind if I observe your technique?'",
          options: ["Would", "Will", "Can", "Should"],
          a: "Would",
          successTip: "👨‍⚕️ Excellent! **WOULD YOU MIND** = extremely polite request!"
        },
        {
          type: "mc",
          q: "**Doctor to medical inspector:** '___ you care to review our sterilization procedures?'",
          options: ["Would", "Will", "Can", "May"],
          a: "Would",
          successTip: "🔍 Perfect! **WOULD YOU CARE TO** = very formal invitation!"
        },
        {
          type: "mc",
          q: "**Receptionist to ambassador's family:** '___ you prefer the executive suite?'",
          options: ["Would", "Will", "Can", "Should"],
          a: "Would",
          successTip: "🏨 Outstanding! **WOULD YOU PREFER** = formal preference inquiry!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // ⚖️ SET 5: CAN vs COULD - Politeness Levels (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s5",
      title: "Set 5: Can vs Could - Politeness",
      icon: "⚖️",
      context: `**Understanding Politeness - Can vs Could**

¡DIFERENCIA IMPORTANTE!

🙋 **CAN YOU** = informal, directo (amigos, familia, colegas)
🎩 **COULD YOU** = educado, cortés (pacientes, desconocidos, formal)

**Understanding Politeness - Can vs Could**

IMPORTANT DIFFERENCE!

🙋 **CAN YOU** = informal, direct (friends, family, colleagues)
🎩 **COULD YOU** = polite, courteous (patients, strangers, formal)`,

      questions: [
        {
          type: "match",
          q: "**Match: Situación → Forma correcta**",
          pairs: [
            { left: "Informal (to friend)", right: "Can you help me?" },
            { left: "Polite (to patient)", right: "Could you open your mouth?" },
            { left: "Informal (to colleague)", right: "Can you pass me that?" },
            { left: "Polite (to stranger)", right: "Could you tell me the time?" }
          ],
          successTip: "⚖️ Perfect! **CAN** = casual, **COULD** = polite!"
        },
        {
          type: "mc",
          q: "**Nurse to best friend (who is visiting):** '___ you bring me a coffee when you come back?'",
          options: ["Can", "Could"],
          a: "Can",
          successTip: "☕ **CAN** = informal with friends!"
        },
        {
          type: "mc",
          q: "**Nurse to elderly patient she just met:** '___ you remove your shoes, please?'",
          options: ["Could", "Can"],
          a: "Could",
          successTip: "👞 **COULD** = polite with patients!"
        },
        {
          type: "mc",
          q: "**Doctor to intern (works together daily):** '___ you grab that chart for me?'",
          options: ["Can", "Could"],
          a: "Can",
          successTip: "📋 **CAN** = casual between colleagues!"
        },
        {
          type: "mc",
          q: "**Receptionist to new patient (first visit):** '___ you provide your insurance information?'",
          options: ["Could", "Can"],
          a: "Could",
          successTip: "🆔 **COULD** = professional and polite!"
        },
        {
          type: "order",
          q: "**Traduce (informal a un amigo):** '¿Puedes cerrar la ventana?'",
          words: ["Can", "you", "close", "the", "window?"],
          extra: ["Could", "Would", "Will"],
          a: ["Can", "you", "close", "the", "window?"],
          successTip: "💡 **CAN YOU** = perfect for informal situations with friends!"
        },
        {
          type: "order",
          q: "**Traduce (educado a un paciente):** '¿Podría respirar profundamente, por favor?'",
          words: ["Could", "you", "breathe", "deeply,", "please?"],
          extra: ["Can", "Would", "Will"],
          a: ["Could", "you", "breathe", "deeply,", "please?"],
          successTip: "💡 **COULD YOU + PLEASE** = polite medical request!"
        },
        {
          type: "mc",
          q: "**Parent to child (at hospital):** '___ you sit still while the nurse checks your ears?'",
          options: ["Can", "Could"],
          a: "Can",
          successTip: "👂 **CAN** = direct instruction to family!"
        },
        {
          type: "mc",
          q: "**Doctor to patient's spouse (formal):** '___ you wait outside during the procedure?'",
          options: ["Could", "Can"],
          a: "Could",
          successTip: "🚪 **COULD** = respectful request to family!"
        },
        {
          type: "mc",
          q: "**Lab tech to coworker:** '___ you hand me those test tubes?'",
          options: ["Can", "Could"],
          a: "Can",
          successTip: "🧪 **CAN** = casual workplace request!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🔀 SET 6: WILL vs WOULD - Politeness Levels (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s6",
      title: "Set 6: Will vs Would - Politeness",
      icon: "🔀",
      context: `**Understanding Politeness - Will vs Would**

¡DIFERENCIA EN FORMALIDAD!

💬 **WILL YOU** = neutro, directo (trabajo, situaciones normales)
👔 **WOULD YOU** = formal, muy cortés (superiores, VIPs, formal)

**Understanding Politeness - Will vs Would**

DIFFERENCE IN FORMALITY!

💬 **WILL YOU** = neutral, direct (work, normal situations)
👔 **WOULD YOU** = formal, very courteous (superiors, VIPs, formal)`,

      questions: [
        {
          type: "match",
          q: "**Match: Nivel de formalidad → Forma correcta**",
          pairs: [
            { left: "Neutral (work)", right: "Will you finish this today?" },
            { left: "Formal (to boss)", right: "Would you review my work?" },
            { left: "Neutral (colleague)", right: "Will you help me move this?" },
            { left: "Formal (to VIP)", right: "Would you like to sit here?" }
          ],
          successTip: "🔀 Perfect! **WILL** = neutral, **WOULD** = formal!"
        },
        {
          type: "mc",
          q: "**Nurse to supervisor:** '___ you approve my vacation request?'",
          options: ["Would", "Will"],
          a: "Would",
          successTip: "📅 **WOULD** = formal request to superior!"
        },
        {
          type: "mc",
          q: "**Manager to employee (same level):** '___ you organize the supply room?'",
          options: ["Will", "Would"],
          a: "Will",
          successTip: "📦 **WILL** = neutral work assignment!"
        },
        {
          type: "mc",
          q: "**Junior doctor to chief of surgery:** '___ you mentor me on this procedure?'",
          options: ["Would", "Will"],
          a: "Would",
          successTip: "👨‍⚕️ **WOULD** = respectful request to senior!"
        },
        {
          type: "mc",
          q: "**Coordinator to team member:** '___ you present at the staff meeting?'",
          options: ["Will", "Would"],
          a: "Will",
          successTip: "🎤 **WILL** = neutral meeting request!"
        },
        {
          type: "order",
          q: "**Traduce (neutro a colega):** '¿Llamarás al proveedor mañana?'",
          words: ["Will", "you", "call", "the", "supplier", "tomorrow?"],
          extra: ["Would", "Could", "Can"],
          a: ["Will", "you", "call", "the", "supplier", "tomorrow?"],
          successTip: "💡 **WILL YOU** = neutral work request between equals!"
        },
        {
          type: "order",
          q: "**Traduce (formal al director):** '¿Podría usted asistir a la reunión?'",
          words: ["Would", "you", "be", "able", "to", "attend", "the", "meeting?"],
          extra: ["Will", "Can", "Could"],
          a: ["Would", "you", "be", "able", "to", "attend", "the", "meeting?"],
          successTip: "💡 **WOULD YOU BE ABLE TO** = extremely formal request!"
        },
        {
          type: "mc",
          q: "**Secretary to CEO:** '___ you prefer morning or afternoon for the appointment?'",
          options: ["Would", "Will"],
          a: "Would",
          successTip: "⏰ **WOULD** = formal inquiry to executive!"
        },
        {
          type: "mc",
          q: "**Supervisor to staff:** '___ you all submit your timesheets by Friday?'",
          options: ["Will", "Would"],
          a: "Will",
          successTip: "📄 **WILL** = neutral instruction to group!"
        },
        {
          type: "mc",
          q: "**Nurse to hospital board member:** '___ you like a tour of the new facility?'",
          options: ["Would", "Will"],
          a: "Would",
          successTip: "🏥 **WOULD** = formal offer to important person!"
        },
        {
          type: "mc",
          q: "**Manager to maintenance team:** '___ you fix the air conditioning today?'",
          options: ["Will", "Would"],
          a: "Will",
          successTip: "❄️ **WILL** = neutral work order!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🙏 SET 7: WOULD YOU MIND...? - Most Formal (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s7",
      title: "Set 7: Would You Mind...? - Most Formal",
      icon: "🙏",
      context: `**The Most Polite Form - Would You Mind...?**

"WOULD YOU MIND...?" es LA FORMA MÁS EDUCADA de hacer peticiones. Se usa con -ING (gerundio).

⚠️ IMPORTANTE: "Would you mind opening...?" = "¿Te importaría abrir...?"

**The Most Polite Form - Would You Mind...?**

"WOULD YOU MIND...?" is THE MOST POLITE form of making requests. Used with -ING (gerund).

⚠️ IMPORTANT: "Would you mind opening...?" = "Would it bother you to open...?"`,

      questions: [
        {
          type: "mc",
          q: "**Doctor to patient's family (very polite):** 'Would you mind ___ in the waiting room?'",
          options: ["waiting", "wait", "to wait", "waited"],
          a: "waiting",
          successTip: "🙏 **WOULD YOU MIND + -ING** = most polite form!"
        },
        {
          type: "mc",
          q: "**Nurse to VIP patient:** 'Would you mind ___ your arm, please?'",
          options: ["rolling up", "roll up", "to roll up", "rolled up"],
          a: "rolling up",
          successTip: "💉 Perfect! **MIND + gerund (-ING)** = extremely polite!"
        },
        {
          type: "input",
          q: "**Receptionist to important visitor:** 'Would you mind ___ this form?'",
          alts: ["filling out", "completing"],
          a: "filling out",
          successTip: "📋 Great! Remember: MIND + verb-ING!"
        },
        {
          type: "mc",
          q: "**Doctor to hospital inspector:** 'Would you mind ___ until tomorrow? We need to prepare.'",
          options: ["coming back", "come back", "to come back", "came back"],
          a: "coming back",
          successTip: "🔙 Excellent! **WOULD YOU MIND -ING** = very respectful!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Le importaría esperar un momento?'",
          words: ["Would", "you", "mind", "waiting", "a", "moment?"],
          extra: ["wait", "to", "waited"],
          a: ["Would", "you", "mind", "waiting", "a", "moment?"],
          successTip: "💡 **WOULD YOU MIND + -ING** = formula for extreme politeness!"
        },
        {
          type: "mc",
          q: "**Administrator to board chairman:** 'Would you mind ___ our new policy proposal?'",
          options: ["reviewing", "review", "to review", "reviewed"],
          a: "reviewing",
          successTip: "📊 Perfect! **MIND + gerund** = most formal request!"
        },
        {
          type: "input",
          q: "**Nurse to elderly patient:** 'Would you mind ___ more slowly for me?'",
          alts: ["walking", "moving"],
          a: "walking",
          successTip: "🚶 Great! **WOULD YOU MIND -ING** shows respect!"
        },
        {
          type: "mc",
          q: "**Doctor to medical consultant:** 'Would you mind ___ a second opinion on this case?'",
          options: ["giving", "give", "to give", "gave"],
          a: "giving",
          successTip: "🩺 Excellent! **MIND -ING** = polite professional request!"
        },
        {
          type: "mc",
          q: "**Receptionist to ambassador:** 'Would you mind ___ your contact information?'",
          options: ["providing", "provide", "to provide", "provided"],
          a: "providing",
          successTip: "📱 Perfect! **WOULD YOU MIND -ING** = diplomatic courtesy!"
        },
        {
          type: "order",
          q: "**Traduce:** '¿Le importaría repetir eso, por favor?'",
          words: ["Would", "you", "mind", "repeating", "that,", "please?"],
          extra: ["repeat", "to", "repeated"],
          a: ["Would", "you", "mind", "repeating", "that,", "please?"],
          successTip: "💡 Always use **-ING** after MIND, never infinitive!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏥 SET 8: Mixed Medical Requests (B1+/B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s8",
      title: "Set 8: Medical Requests - Mixed",
      icon: "🏥",
      context: `**Hospital Scenarios - Choosing the Right Form**

En el hospital, debes elegir la forma correcta según: QUIÉN es la persona, QUÉ tan formal es la situación, y QUÉ estás pidiendo.

**Hospital Scenarios - Choosing the Right Form**

In the hospital, you must choose the correct form based on: WHO the person is, HOW formal the situation is, and WHAT you're asking.`,

      questions: [
        {
          type: "mc",
          q: "**Emergency! Nurse to colleague:** '___ you get the defibrillator NOW!' (Urgente, informal)",
          options: ["Can", "Could", "Would", "Will"],
          a: "Can",
          successTip: "🚨 **CAN** = quickest, most direct in emergencies!"
        },
        {
          type: "mc",
          q: "**Doctor to nervous child patient:** '___ you show me where it hurts?' (Gentle, polite)",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "👶 **COULD** = gentle and reassuring with children!"
        },
        {
          type: "mc",
          q: "**Nurse to patient's spouse:** 'Would you mind ___ outside during the procedure?' (Very polite)",
          options: ["waiting", "wait", "to wait", "waited"],
          a: "waiting",
          successTip: "🚪 **WOULD YOU MIND -ING** = most respectful to family!"
        },
        {
          type: "mc",
          q: "**Administrator to entire staff:** '___ you all attend the safety training?' (Neutral, group)",
          options: ["Will", "Would", "Can", "Could"],
          a: "Will",
          successTip: "👥 **WILL** = neutral instruction to group!"
        },
        {
          type: "order",
          q: "**Traduce (urgente a colega):** '¿Puedes llamar a cirugía inmediatamente?'",
          words: ["Can", "you", "call", "surgery", "immediately?"],
          extra: ["Could", "Would", "Will"],
          a: ["Can", "you", "call", "surgery", "immediately?"],
          successTip: "💡 In emergencies, **CAN** is fastest and most direct!"
        },
        {
          type: "mc",
          q: "**Junior doctor to chief surgeon (formal):** '___ you supervise my first solo operation?' (Respectful)",
          options: ["Would", "Will", "Can", "Could"],
          a: "Would",
          successTip: "👨‍⚕️ **WOULD** = formal request to superior!"
        },
        {
          type: "mc",
          q: "**Nurse to elderly patient (polite):** '___ you take this medication with water?' (Gentle instruction)",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "💊 **COULD** = polite medical instruction!"
        },
        {
          type: "input",
          q: "**Receptionist to angry patient (very diplomatic):** 'Would you mind ___ this complaint form?' (Calming)",
          alts: ["filling out", "completing"],
          a: "filling out",
          successTip: "📋 **WOULD YOU MIND -ING** = de-escalates tense situations!"
        },
        {
          type: "mc",
          q: "**Paramedic to conscious accident victim:** '___ you tell me your name?' (Polite but urgent)",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "🚑 **COULD** = polite even in urgent situations!"
        },
        {
          type: "mc",
          q: "**Pharmacist to patient with prescription:** '___ you sign here and show ID?' (Professional standard)",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "✍️ **COULD** = professional pharmacy protocol!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🌍 SET 9: Everyday Polite Requests (B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s9",
      title: "Set 9: Everyday Polite Requests",
      icon: "🌍",
      context: `**Beyond the Hospital - Real Life Politeness**

Las peticiones educadas se usan en TODAS partes: restaurantes, tiendas, aeropuertos, hoteles. Elegir la forma correcta muestra tu nivel de inglés y educación.

**Beyond the Hospital - Real Life Politeness**

Polite requests are used EVERYWHERE: restaurants, stores, airports, hotels. Choosing the right form shows your English level and manners.`,

      questions: [
        {
          type: "mc",
          q: "**At restaurant to waiter:** '___ you bring me the check, please?'",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "🍽️ **COULD** = standard polite restaurant request!"
        },
        {
          type: "mc",
          q: "**At airport to stranger:** 'Would you mind ___ my luggage while I find my ticket?'",
          options: ["watching", "watch", "to watch", "watched"],
          a: "watching",
          successTip: "✈️ **WOULD YOU MIND -ING** = polite to strangers!"
        },
        {
          type: "mc",
          q: "**To taxi driver:** '___ you take me to the train station?'",
          options: ["Can", "Could", "Would", "Will"],
          a: "Can",
          successTip: "🚕 **CAN** = acceptable for simple service requests!"
        },
        {
          type: "mc",
          q: "**At hotel reception (formal):** '___ you like a room with a view or a quiet room?'",
          options: ["Would", "Will", "Can", "Could"],
          a: "Would",
          successTip: "🏨 **WOULD YOU LIKE** = formal hotel service!"
        },
        {
          type: "order",
          q: "**Traduce (educado en tienda):** '¿Podría mostrarme ese abrigo, por favor?'",
          words: ["Could", "you", "show", "me", "that", "coat,", "please?"],
          extra: ["Can", "Would", "Will"],
          a: ["Could", "you", "show", "me", "that", "coat,", "please?"],
          successTip: "💡 **COULD + PLEASE** = polite shopping request!"
        },
        {
          type: "mc",
          q: "**To office colleague (asking favor):** '___ you cover my desk while I'm at lunch?'",
          options: ["Can", "Could", "Would", "Will"],
          a: "Can",
          successTip: "💼 **CAN** = casual workplace favor!"
        },
        {
          type: "mc",
          q: "**To neighbor (polite):** 'Would you mind ___ your music down? It's quite late.'",
          options: ["turning", "turn", "to turn", "turned"],
          a: "turning",
          successTip: "🔊 **WOULD YOU MIND -ING** = tactful complaint!"
        },
        {
          type: "mc",
          q: "**At bank to teller:** '___ you help me open a new account?'",
          options: ["Could", "Can", "Will", "Would"],
          a: "Could",
          successTip: "🏦 **COULD** = professional banking request!"
        },
        {
          type: "input",
          q: "**To building manager (formal):** '___ you look into the heating problem?'",
          alts: ["Would", "Could"],
          a: "Would",
          successTip: "🏢 **WOULD** = formal tenant-to-manager request!"
        },
        {
          type: "mc",
          q: "**At gym to trainer:** '___ you show me how to use this machine?'",
          options: ["Can", "Could", "Would", "Will"],
          a: "Can",
          successTip: "🏋️ **CAN** = informal learning request!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏆 SET 10: Mastery - Cultural Appropriateness (B2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u4-s10",
      title: "Set 10: Mastery - Cultural Context",
      icon: "🏆",
      context: `**🏆 MASTERY - Cultural and Contextual Appropriateness**

El nivel más alto: saber NO SOLO qué forma usar, sino POR QUÉ y CUÁNDO. Considerando jerarquía, urgencia, relación, y contexto cultural.

**🏆 MASTERY - Cultural and Contextual Appropriateness**

The highest level: knowing NOT ONLY which form to use, but WHY and WHEN. Considering hierarchy, urgency, relationship, and cultural context.`,

      questions: [
        {
          type: "mc",
          q: "**CONTEXT: You're a junior employee. Your CEO walks by. You need help.** Which is MOST appropriate?",
          options: [
            "Would you mind helping me with this report?",
            "Can you help me with this report?",
            "Will you help me with this report?",
            "Help me with this report."
          ],
          a: "Would you mind helping me with this report?",
          successTip: "🏆 **WOULD YOU MIND** = most respectful to CEO!"
        },
        {
          type: "mc",
          q: "**CONTEXT: Medical emergency. Patient not breathing. You need oxygen NOW.** Which is MOST appropriate?",
          options: [
            "Can you get oxygen?!",
            "Would you mind getting oxygen?",
            "Could you please get oxygen?",
            "Will you get oxygen?"
          ],
          a: "Can you get oxygen?!",
          successTip: "🚨 In emergencies, **CAN** = fastest, clearest, most direct!"
        },
        {
          type: "mc",
          q: "**CONTEXT: You're a doctor. An elderly patient is confused and scared.** Which is MOST appropriate?",
          options: [
            "Could you sit down here for me?",
            "Can you sit down?",
            "Will you sit?",
            "Sit down."
          ],
          a: "Could you sit down here for me?",
          successTip: "👴 **COULD** = gentle, reassuring to frightened patients!"
        },
        {
          type: "match",
          q: "**Match: Situation → Most Appropriate Form**",
          pairs: [
            { left: "Emergency (colleague)", right: "Can you...?" },
            { left: "Polite (patient)", right: "Could you...?" },
            { left: "Neutral (coworker)", right: "Will you...?" },
            { left: "Very formal (VIP)", right: "Would you mind...?" }
          ],
          successTip: "🎯 Mastery! You understand contextual appropriateness!"
        },
        {
          type: "mc",
          q: "**CONTEXT: You spilled coffee on your boss's important document.** Which shows MOST remorse and respect?",
          options: [
            "Would you mind if I print you a new copy?",
            "Can I print you a new copy?",
            "Will I print you a new copy?",
            "I'll print you a new copy."
          ],
          a: "Would you mind if I print you a new copy?",
          successTip: "😰 **WOULD YOU MIND IF** = most apologetic and formal!"
        },
        {
          type: "order",
          q: "**Traduce (cultura británica, muy formal):** '¿Sería usted tan amable de revisar esto?'",
          words: ["Would", "you", "be", "so", "kind", "as", "to", "review", "this?"],
          extra: ["Will", "Can", "Could"],
          a: ["Would", "you", "be", "so", "kind", "as", "to", "review", "this?"],
          successTip: "💡 **WOULD YOU BE SO KIND AS TO** = extremely formal British style!"
        },
        {
          type: "mc",
          q: "**CONTEXT: You're training a new employee. Teaching them procedures.** Which establishes friendly but professional tone?",
          options: [
            "Can you follow these steps?",
            "Would you mind following these steps?",
            "Could you follow these steps?",
            "Will you follow these steps?"
          ],
          a: "Can you follow these steps?",
          successTip: "📚 **CAN** = friendly, approachable training style!"
        },
        {
          type: "mc",
          q: "**CONTEXT: International conference. You're asking keynote speaker (famous professor) a question.** Most appropriate?",
          options: [
            "Would you mind elaborating on that point?",
            "Can you elaborate?",
            "Will you elaborate?",
            "Could you elaborate?"
          ],
          a: "Would you mind elaborating on that point?",
          successTip: "🎤 **WOULD YOU MIND** = appropriate for distinguished speakers!"
        },
        {
          type: "mc",
          q: "**CONTEXT: You're a manager. Employee did something wrong repeatedly. You're frustrated but professional.** Which is firm but polite?",
          options: [
            "Will you please follow the correct procedure from now on?",
            "Can you follow the procedure?",
            "Would you mind following the procedure?",
            "Could you follow the procedure?"
          ],
          a: "Will you please follow the correct procedure from now on?",
          successTip: "💼 **WILL YOU PLEASE** = firm but professional directive!"
        },
        {
          type: "mc",
          q: "**🏆 FINAL MASTERY: You're a hospital administrator. Situation: Budget crisis. You must ask the Board of Directors to approve emergency funding. The chairman is traditional and values formality. Which demonstrates highest cultural and professional competence?**",
          options: [
            "Would you be willing to consider approving emergency funding for patient care?",
            "Can you approve emergency funding?",
            "Will you approve emergency funding?",
            "Could you approve emergency funding?"
          ],
          a: "Would you be willing to consider approving emergency funding for patient care?",
          successTip: "🎉 **INCREDIBLE!** You've mastered: **WOULD YOU BE WILLING TO CONSIDER** = most diplomatic, formal, and respectful form for high-stakes professional situations! You understand hierarchy, urgency, cultural context, and relationship dynamics! **UNIT 4 COMPLETE!** 🏆✨"
        }
      ]
    }
  ],

  // ═══════════════════════════════════════════════════════════════
  // 📖 READING SET (m9-u4-reading)
  // ═══════════════════════════════════════════════════════════════

  readingSet: {
    id: "m9-u4-reading",
    title: "Reading Practice: Polite Requests",
    category: "reading",
    
    passages: [
      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 1: Doctor's Office
      // ─────────────────────────────────────────────────────────
      {
        context: `**Doctor's Office - Making Polite Requests**

Una enfermera hablando con diferentes personas en el hospital.

**Doctor's Office - Making Polite Requests**

A nurse talking to different people at the hospital.`,

        text: `To her colleague: "**Can** you help me move this patient?" To the patient: "**Could** you roll up your sleeve, please?" To the hospital director: "**Would** you review our new protocol?"`,

        questions: [
          {
            type: "mc",
            q: "What does the nurse say to her colleague?",
            options: ["Can you help me?", "Could you help me?", "Would you help me?", "Will you help me?"],
            a: "Can you help me?",
            successTip: "🙋 **CAN** = informal request to colleague!"
          },
          {
            type: "mc",
            q: "What does the nurse say to the patient?",
            options: ["Could you roll up your sleeve?", "Can you roll up your sleeve?", "Will you roll up your sleeve?", "Would you roll up your sleeve?"],
            a: "Could you roll up your sleeve?",
            successTip: "🎩 **COULD** = polite request to patient!"
          },
          {
            type: "mc",
            q: "What does the nurse say to the director?",
            options: ["Would you review?", "Will you review?", "Can you review?", "Could you review?"],
            a: "Would you review?",
            successTip: "👔 **WOULD** = formal request to superior!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 2: Restaurant Requests
      // ─────────────────────────────────────────────────────────
      {
        context: `**Restaurant - Customer Making Requests**

Un cliente haciendo diferentes peticiones en un restaurante.

**Restaurant - Customer Making Requests**

A customer making different requests at a restaurant.`,

        text: `Customer to waiter: "**Could** you bring me the menu, please?" To his friend: "**Can** you pass the salt?" To the manager about a problem: "**Would** you mind speaking to the chef about this?"`,

        questions: [
          {
            type: "mc",
            q: "What does the customer say to the waiter?",
            options: ["Could you bring the menu?", "Can you bring the menu?", "Will you bring the menu?", "Would you bring the menu?"],
            a: "Could you bring the menu?",
            successTip: "🎩 **COULD** = polite restaurant request!"
          },
          {
            type: "mc",
            q: "What does the customer say to his friend?",
            options: ["Can you pass the salt?", "Could you pass the salt?", "Would you pass the salt?", "Will you pass the salt?"],
            a: "Can you pass the salt?",
            successTip: "🙋 **CAN** = informal to friend!"
          },
          {
            type: "mc",
            q: "What form does he use with the manager?",
            options: ["Would you mind", "Can you", "Could you", "Will you"],
            a: "Would you mind",
            successTip: "🙏 **WOULD YOU MIND** = most polite for complaints!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 3: Office Requests
      // ─────────────────────────────────────────────────────────
      {
        context: `**Office Environment - Different Levels of Formality**

Peticiones en la oficina según la relación entre personas.

**Office Environment - Different Levels of Formality**

Requests in the office based on relationship between people.`,

        text: `Manager to employee: "**Will** you finish this report by tomorrow?" Employee to manager: "**Would** you review my proposal?" Colleague to colleague: "**Can** you email me that file?"`,

        questions: [
          {
            type: "mc",
            q: "What does the manager say to the employee?",
            options: ["Will you finish?", "Would you finish?", "Can you finish?", "Could you finish?"],
            a: "Will you finish?",
            successTip: "💬 **WILL** = neutral work instruction!"
          },
          {
            type: "mc",
            q: "What does the employee say to the manager?",
            options: ["Would you review?", "Will you review?", "Can you review?", "Could you review?"],
            a: "Would you review?",
            successTip: "👔 **WOULD** = formal upward request!"
          },
          {
            type: "mc",
            q: "What do colleagues say to each other?",
            options: ["Can you email?", "Could you email?", "Would you email?", "Will you email?"],
            a: "Can you email?",
            successTip: "🙋 **CAN** = casual between equals!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 4: Emergency Situation
      // ─────────────────────────────────────────────────────────
      {
        context: `**Hospital Emergency - Urgency Changes Request Form**

En emergencias, se usan formas más directas.

**Hospital Emergency - Urgency Changes Request Form**

In emergencies, more direct forms are used.`,

        text: `Normal situation: "**Could** you bring oxygen?" Emergency situation: "**Can** you get oxygen NOW?!" The nurse uses **can** because it's faster and more direct in emergencies.`,

        questions: [
          {
            type: "mc",
            q: "What form is used in normal situations?",
            options: ["Could", "Can", "Would", "Will"],
            a: "Could",
            successTip: "🎩 **COULD** = polite in normal situations!"
          },
          {
            type: "mc",
            q: "What form is used in emergencies?",
            options: ["Can", "Could", "Would", "Will"],
            a: "Can",
            successTip: "🚨 **CAN** = fastest and clearest in emergencies!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 5: Hotel Formality
      // ─────────────────────────────────────────────────────────
      {
        context: `**Luxury Hotel - Maximum Formality**

En hoteles de lujo, se usa el lenguaje más formal posible.

**Luxury Hotel - Maximum Formality**

In luxury hotels, the most formal language possible is used.`,

        text: `Receptionist to VIP guest: "**Would** you like a room with an ocean view?" "**Would** you prefer morning or evening housekeeping?" "**Would** you mind completing this registration form?" Very formal and respectful.`,

        questions: [
          {
            type: "mc",
            q: "What form does the receptionist use most?",
            options: ["Would", "Will", "Can", "Could"],
            a: "Would",
            successTip: "👔 **WOULD** = maximum formality for VIP service!"
          },
          {
            type: "mc",
            q: "Which form is also used for extreme politeness?",
            options: ["Would you mind", "Can you", "Will you", "Could you"],
            a: "Would you mind",
            successTip: "🙏 **WOULD YOU MIND** = most formal request!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 6: Cultural Differences
      // ─────────────────────────────────────────────────────────
      {
        context: `**Cultural Context - British vs American**

Las preferencias de formalidad pueden variar por cultura.

**Cultural Context - British vs American**

Formality preferences can vary by culture.`,

        text: `In Britain, people often use **could**, **would**, and **would you mind** more frequently. In America, **can** and **will** are more common. Both are correct, but British English tends to be more formal.`,

        questions: [
          {
            type: "mc",
            q: "Which forms do British people use more?",
            options: ["Could, would, would you mind", "Can and will", "Must and should", "May and might"],
            a: "Could, would, would you mind",
            successTip: "🇬🇧 British English = more formal tendencies!"
          },
          {
            type: "mc",
            q: "Which forms are more common in American English?",
            options: ["Can and will", "Could and would", "Would you mind", "Should and must"],
            a: "Can and will",
            successTip: "🇺🇸 American English = more direct style!"
          },
          {
            type: "mc",
            q: "Which style tends to be more formal?",
            options: ["British", "American", "Both equal", "Neither"],
            a: "British",
            successTip: "🎩 British English = generally more formal!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 7: Workplace Hierarchy
      // ─────────────────────────────────────────────────────────
      {
        context: `**Understanding Workplace Hierarchy**

La forma que usas muestra tu comprensión de jerarquía profesional.

**Understanding Workplace Hierarchy**

The form you use shows your understanding of professional hierarchy.`,

        text: `To your boss: "**Would** you..." To colleagues: "**Can** you..." or "**Will** you..." To subordinates: "**Can** you..." or "**Will** you..." **Never** use "can" with superiors in formal settings.`,

        questions: [
          {
            type: "mc",
            q: "What should you use with your boss?",
            options: ["Would", "Can", "Will", "Could"],
            a: "Would",
            successTip: "👔 **WOULD** = respect for superiors!"
          },
          {
            type: "mc",
            q: "What should you AVOID with superiors in formal settings?",
            options: ["Can", "Would", "Could", "Will"],
            a: "Can",
            successTip: "⚠️ **CAN** = too informal for bosses!"
          }
        ]
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // 🏆 REVIEW SET (m9-u4-review)
  // ═══════════════════════════════════════════════════════════════

  reviewSet: {
    id: "m9-u4-review",
    title: "Unit Review: Requests Mastery",
    category: "review",
    context: `**🏆 UNIT 4 FINAL REVIEW - Making Polite Requests**

Repaso final de la Unidad 4. Demuestra que dominas:

🙋 **CAN YOU** = informal (amigos, familia, colegas)
🎩 **COULD YOU** = educado (pacientes, desconocidos)
💬 **WILL YOU** = neutro (trabajo normal)
👔 **WOULD YOU** = formal (superiores, VIPs)
🙏 **WOULD YOU MIND -ING** = muy formal (máxima cortesía)

**🏆 UNIT 4 FINAL REVIEW - Making Polite Requests**

Final review of Unit 4. Prove you master:

🙋 **CAN YOU** = informal (friends, family, colleagues)
🎩 **COULD YOU** = polite (patients, strangers)
💬 **WILL YOU** = neutral (normal work)
👔 **WOULD YOU** = formal (superiors, VIPs)
🙏 **WOULD YOU MIND -ING** = very formal (maximum courtesy)`,

    questions: [
      {
        type: "mc",
        q: "**To your best friend:** '___ you help me move this weekend?'",
        options: ["Can", "Could", "Would", "Will"],
        a: "Can",
        successTip: "🙋 **CAN** = perfect for friends!"
      },
      {
        type: "mc",
        q: "**To a patient:** '___ you lie down on the table, please?'",
        options: ["Could", "Can", "Will", "Would"],
        a: "Could",
        successTip: "🎩 **COULD** = polite to patients!"
      },
      {
        type: "mc",
        q: "**To coworker (neutral):** '___ you finish this by Friday?'",
        options: ["Will", "Would", "Can", "Could"],
        a: "Will",
        successTip: "💬 **WILL** = neutral work request!"
      },
      {
        type: "mc",
        q: "**To your boss:** '___ you review my presentation?'",
        options: ["Would", "Will", "Can", "Could"],
        a: "Would",
        successTip: "👔 **WOULD** = formal to superior!"
      },
      {
        type: "mc",
        q: "**To VIP (most formal):** 'Would you mind ___ this form?'",
        options: ["signing", "sign", "to sign", "signed"],
        a: "signing",
        successTip: "🙏 **WOULD YOU MIND + -ING** = maximum politeness!"
      },
      {
        type: "match",
        q: "**REVIEW: Match situation to correct form**",
        pairs: [
          { left: "Informal (friend)", right: "Can you...?" },
          { left: "Polite (patient)", right: "Could you...?" },
          { left: "Neutral (coworker)", right: "Will you...?" },
          { left: "Formal (boss)", right: "Would you...?" }
        ],
        successTip: "🎯 Perfect! You understand all politeness levels!"
      },
      {
        type: "order",
        q: "**Traduce (informal):** '¿Puedes cerrar la puerta?'",
        words: ["Can", "you", "close", "the", "door?"],
        extra: ["Could", "Would", "Will"],
        a: ["Can", "you", "close", "the", "door?"],
        successTip: "💡 **CAN YOU** = simple informal request!"
      },
      {
        type: "order",
        q: "**Traduce (muy formal):** '¿Le importaría esperar un momento?'",
        words: ["Would", "you", "mind", "waiting", "a", "moment?"],
        extra: ["wait", "to", "waited"],
        a: ["Would", "you", "mind", "waiting", "a", "moment?"],
        successTip: "💡 Always **-ING** after MIND!"
      },
      {
        type: "mc",
        q: "**Emergency situation (colleague):** '___ you get the crash cart NOW?!'",
        options: ["Can", "Could", "Would", "Will"],
        a: "Can",
        successTip: "🚨 **CAN** = fastest in emergencies!"
      },
      {
        type: "mc",
        q: "**To nervous child patient:** '___ you show me where it hurts?'",
        options: ["Could", "Can", "Will", "Would"],
        a: "Could",
        successTip: "👶 **COULD** = gentle with children!"
      },
      {
        type: "mc",
        q: "**To hotel guest (formal):** '___ you prefer a smoking or non-smoking room?'",
        options: ["Would", "Will", "Can", "Could"],
        a: "Would",
        successTip: "🏨 **WOULD** = formal hotel service!"
      },
      {
        type: "mc",
        q: "**FINAL MASTERY:** You need to ask the CEO (who values formality) to approve emergency budget. Most appropriate?",
        options: [
          "Would you be willing to consider this request?",
          "Can you approve this?",
          "Will you approve this?",
          "Could you approve this?"
        ],
        a: "Would you be willing to consider this request?",
        successTip: "🎉 **PERFECT!** You've mastered request forms: **WOULD YOU BE WILLING TO** = most diplomatic and formal! You understand hierarchy, context, and cultural appropriateness! **CONGRATULATIONS - UNIT 4 MAKING REQUESTS COMPLETE!** 🏆✨"
      }
    ]
  }
};
